/**
 * QorTrace · useQelliWarmPulse — Iter198.
 *
 * Reusable hook that fires a `qt-qelli-pulse` event ~3.2s after the
 * page mounts when the visitor arrives from a "warm" source. The
 * `QelliAvatar3D` component listens for that event and shows a
 * contextual speech bubble offering Qelli's help.
 *
 * "Warm" =
 *   • `?ref=` is one of {linkedin, linkedin-dm, twitter, email}, OR
 *   • `document.referrer` belongs to a social/aggregator surface we
 *     consider a warm inbound (LinkedIn / X / Reddit / HN / t.co).
 *
 * Usage:
 *
 *   useQelliWarmPulse({
 *     enabled:    !!cert,                        // gate behind data
 *     source:     `verify-${cert.cert_id}`,      // sessionStorage one-shot key
 *     intentText: "Want me to explain what this audit found?",
 *     prompt:     "Explain audit XYZ ...",
 *     onFire:     ({channel}) => trackEvent("...", {...}),
 *   });
 *
 * The avatar enforces a per-source one-shot via sessionStorage, so
 * re-renders / re-loads don't re-nag the visitor.
 */
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { trackPulseEvent, rememberPulseSource } from "../lib/qelliPulseFunnel";


// Channels we treat as "warm inbound" — landings that arrive with one
// of these `?ref=` values are likely buyer-shaped traffic.
const WARM_REF_CHANNELS = new Set([
  "linkedin", "linkedin-dm", "twitter", "email",
]);

// Hosts whose `document.referrer` we treat as warm inbound. Domain
// suffixes are checked, so subdomains (e.g. m.linkedin.com) match.
const WARM_REFERRER_HOSTS = [
  "linkedin.com", "lnkd.in",
  "twitter.com", "x.com", "t.co",
  "reddit.com",
  "news.ycombinator.com",
];

const DELAY_MS = 3200;


export default function useQelliWarmPulse({
  enabled    = true,
  source,
  intentText,
  prompt,
  delayMs    = DELAY_MS,
  onFire,
}) {
  const [searchParams] = useSearchParams();
  const ref = (searchParams.get("ref") || "").trim().slice(0, 40);

  useEffect(() => {
    if (!enabled || !source || !prompt || !intentText) return undefined;
    if (typeof window === "undefined")                  return undefined;

    const referrerHost = (() => {
      if (!document.referrer) return "";
      try { return new URL(document.referrer).host.toLowerCase(); }
      catch { return ""; }
    })();
    const warmByRef = !!ref && WARM_REF_CHANNELS.has(ref);
    const warmByReferrer = WARM_REFERRER_HOSTS.some((h) =>
      referrerHost === h || referrerHost.endsWith(`.${h}`),
    );
    if (!warmByRef && !warmByReferrer) return undefined;

    const channel = warmByRef ? ref : `referrer:${referrerHost}`;

    const t = window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent("qt-qelli-pulse", {
        detail: { prompt, intentText, source },
      }));
      // Telemetry: count this surface as having fired a pulse, and
      // stash the source on the session so a downstream signup can
      // attribute the conversion back here.
      trackPulseEvent(source, "fired");
      rememberPulseSource(source);
      if (typeof onFire === "function") {
        try { onFire({ channel, source }); } catch { /* swallow */ }
      }
    }, delayMs);
    return () => window.clearTimeout(t);
  }, [enabled, source, prompt, intentText, ref, delayMs, onFire]);
}
